/**
 * SPA hooks for the store birthday setting — /api/store-settings (see
 * apps/api/src/routes/store-settings.ts). A per-tenant floor under every
 * Amazon report backfill window (reimbursements' 18mo walk, sales' 90d
 * walk) so a tenant whose store started in 2026 never walks into
 * pre-existence history. Renders in SettingsPage near the Outreach card.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StoreSettingsResponse, type UpdateStoreSettingsRequest } from '@sourcito/schemas';
import { apiFetch } from './api';

export const STORE_SETTINGS_KEY = ['store-settings'] as const;

export function useStoreSettings() {
  return useQuery({
    queryKey: STORE_SETTINGS_KEY,
    queryFn: () => apiFetch('/api/store-settings', { responseSchema: StoreSettingsResponse }),
  });
}

export function useUpdateStoreSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStoreSettingsRequest) =>
      apiFetch('/api/store-settings', {
        method: 'PUT',
        body: JSON.stringify(input),
        responseSchema: StoreSettingsResponse,
      }),
    onSuccess: (res) => {
      qc.setQueryData(STORE_SETTINGS_KEY, res);
    },
  });
}

/**
 * Pure guard for the date input's "no future dates" rule (the API 400s on
 * this too — this is purely for instant client-side feedback before save).
 * `value` is a <input type="date"> value ("YYYY-MM-DD") or any date string
 * new Date() can parse. Empty/malformed input is never "future" — there's
 * nothing to reject yet. Compares calendar dates at UTC midnight rather
 * than exact timestamps so "today" is never flagged as future regardless
 * of what time of day it currently is.
 */
export function isFutureDate(value: string): boolean {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  const todayUtcMidnight = new Date();
  todayUtcMidnight.setUTCHours(0, 0, 0, 0);
  return parsed.getTime() > todayUtcMidnight.getTime();
}

// ─────────────────────────────────────────────────────────
// Price calibration margin settings (2026-08-25) — tenants.min_margin_pct /
// target_margin_pct. The API stores/returns FRACTIONS 0-1 (FeeProfileData
// convention); the Pricing-targets card displays/edits PERCENTS. These pure
// helpers are the conversion boundary between the two, converted at the
// edge per the build spec — not scattered through the component.
// ─────────────────────────────────────────────────────────

/** Stored fraction -> percent-input display value (0.125 -> 12.5). */
export function marginFractionToPercent(fraction: number): number {
  return fraction * 100;
}

/**
 * Percent-input string -> the fraction the API stores ("12.5" -> 0.125).
 * Returns null for empty/non-numeric input so the caller can distinguish
 * "nothing typed yet" from a real 0 — same contract shape as isFutureDate's
 * empty-string handling above.
 */
export function marginPercentInputToFraction(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n / 100;
}

/**
 * Client-side mirror of the API's 0 <= fraction < 1 rule (routes/
 * store-settings.ts) — instant feedback before the round-trip; the API
 * still re-validates and is the source of truth.
 */
export function isValidMarginFraction(fraction: number | null): boolean {
  return fraction !== null && Number.isFinite(fraction) && fraction >= 0 && fraction < 1;
}
